import React, { useEffect, useMemo, useRef } from 'react'

import {
  Box,
  Color,
  Display,
  FlexDirection,
  ModalProvider,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'

import Loading from 'routes/Menu/Loading'

import type { MenuQueryParams } from '../../context/menuQueryContext'
import { BrowseScreenWrapper } from './BrowseScreenWrapper'
import { CollectionScreenWrapper } from './CollectionScreenWrapper'
import { MenuExperienceCategoryNav } from './MenuExperienceCategoryNav'
import { MenuExperienceRecipeDetailOverlay } from './MenuExperienceRecipeDetailOverlay'
import { MenuExperienceSubHeader } from './MenuExperienceSubHeader'
import { useMenuExperienceNavigation } from './hooks/useMenuExperienceNavigation'
import { MenuBrandingContext, MenuBrandingProvider, getMenuBranding } from './menuBrandingContext'
import { MenuExperienceNavigationProvider } from './menuExperienceNavigationContext'
import { MenuExperienceRecipeDetailProvider } from './menuExperienceRecipeDetailContext'

type MenuExperiencePageProps = {
  location: {
    pathname?: string
    query: MenuQueryParams & Record<string, unknown> & { collection?: string }
    [key: string]: unknown
  }
  [key: string]: unknown
}

const ContentContainer = withCitrusTheme(Box, (theme) => ({
  margin: '1rem auto 0',
  padding: '0 0 1rem',
  [`@media (min-width: ${theme.tokens.breakpoints[1]})`]: {
    padding: '0 30px 1rem',
  },
}))

export const MenuExperiencePage = ({ location }: MenuExperiencePageProps) => {
  const hasMountedRef = useRef(false)
  const { data, error, links, selectCategory } = useMenuExperienceNavigation(
    location.query,
    location.pathname,
  )
  const navigationActions = useMemo(() => ({ selectCategory }), [selectCategory])
  const isCategoryNavVisible = links.some((link) => link.selected)

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true

      return
    }

    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [location.query.collection])

  const hasInitialLoadError = !data && error

  if (hasInitialLoadError) {
    throw error
  }

  if (!data) {
    return (
      <Box data-testid="loading">
        <Loading loading />
      </Box>
    )
  }

  const ScreenWrapper = data.type === 'browse' ? BrowseScreenWrapper : CollectionScreenWrapper
  const menuBranding = getMenuBranding(data.content)
  const shouldGoBackToBrowse = data.type !== 'browse' && !isCategoryNavVisible

  return (
    <Box data-testid="menu-experience-page">
      <MenuExperienceSubHeader
        pathname={location.pathname}
        query={location.query}
        shouldGoBackToBrowse={shouldGoBackToBrowse}
      />
      {isCategoryNavVisible && (
        <Box bg={Color.White} zIndex={ZIndex.Sticky} style={{ position: 'sticky', top: 0 }}>
          <MenuExperienceCategoryNav
            links={links}
            pathname={location.pathname}
            query={location.query}
          />
        </Box>
      )}
      <MenuBrandingProvider value={menuBranding}>
        <MenuExperienceRecipeDetailProvider menuId={data.menuId}>
          <MenuExperienceNavigationProvider value={navigationActions}>
            <MenuBrandingContext.Consumer>
              {({ backgroundColor, backgroundImageUrl, textColor }) => (
                <ContentContainer
                  data-testid="menu-experience-page-content"
                  display={Display.Flex}
                  flexDirection={FlexDirection.Column}
                  maxWidth="1600px"
                  style={{
                    marginTop: backgroundColor || backgroundImageUrl ? 0 : undefined,
                    ...(backgroundColor ? { backgroundColor } : {}),
                    ...(textColor ? { color: textColor } : {}),
                    ...(backgroundImageUrl
                      ? {
                          backgroundImage: `url(${backgroundImageUrl})`,
                          backgroundPosition: 'top center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '100% auto',
                        }
                      : {}),
                  }}
                >
                  <ModalProvider>
                    <ScreenWrapper contentBlocks={data.content} collectionId={data.categoryId} />
                  </ModalProvider>
                </ContentContainer>
              )}
            </MenuBrandingContext.Consumer>
          </MenuExperienceNavigationProvider>
          <MenuExperienceRecipeDetailOverlay collectionId={data.categoryId} />
        </MenuExperienceRecipeDetailProvider>
      </MenuBrandingProvider>
    </Box>
  )
}
